import React, { useMemo, useState } from "react";
import "../styles/shared-ui.css";
import "../styles/job-detail.css";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import { createTask } from "../services/taskService";
import { getJobsByCompanyId } from "../services/jobService";
import { getTaskCategories } from "../services/taskCategoryService";
import { getTaskStatuses } from "../services/taskStatusService";
import SideTabs from "../components/layout/SideTabs";
import RichTextEditor from "../components/ui/RichTextEditor";
import SkillTable from "../components/ui/SkillTable";
import SkillAddModal from "../components/ui/SkillAddModal";
import RaciMatrix, { getRaciValidation } from "../components/ui/RaciMatrix";
import { LEVELS } from "../components/ui/levels";
import { toast } from "../utils/toast";

const FREQUENCY_PERIODS = ["DAY", "WEEK", "MONTH", "YEAR"];

const describeRecurrence = (occurrences, frequencyInterval, frequencyPeriod) => {
  const occ = Number(occurrences);
  const interval = Number(frequencyInterval);
  if (
    !Number.isFinite(occ) ||
    !Number.isFinite(interval) ||
    occ <= 0 ||
    interval <= 0
  ) {
    return "";
  }
  const periodLabel = String(frequencyPeriod || "WEEK").toLowerCase();
  const periodText = interval === 1 ? periodLabel : `${periodLabel}s`;
  const timesText = occ === 1 ? "time" : "times";
  return `${occ} ${timesText} in ${interval} ${periodText}`;
};

const TaskCreatePage = () => {
  const { setActiveSection } = useAppContext();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [errors, setErrors] = useState({});
  const [allJobs, setAllJobs] = useState([]);
  const [raciRows, setRaciRows] = useState([]);
  const [taskCategories, setTaskCategories] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);

  const [form, setForm] = useState({
    task_name: "",
    task_code: "",
    version: "",
    task_company_id: "",
    expected_process_time: "",
    expected_waiting_time: "",
    expected_rework_time: "",
    status_id: "",
    category_id: "",
    task_overview: "",
    frequency_period: "WEEK",
    frequency_interval: "1",
    occurrences: "1",
    skills: [],
  });

  // Helper to load jobs for a specific company
  const loadJobsForCompany = async (companyId) => {
    if (!companyId) {
      setAllJobs([]);
      return;
    }
    try {
      const jobsRes = await getJobsByCompanyId(companyId).catch(() => []);
      setAllJobs(Array.isArray(jobsRes) ? jobsRes : []);
    } catch {
      setAllJobs([]);
    }
  };

  const didInitRef = React.useRef(false);
  React.useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      try {
        setLoading(true);
        const companiesArr = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesArr) ? companiesArr : []);
        const [catsRes, statusRes] = await Promise.all([
          getTaskCategories().catch(() => []),
          getTaskStatuses().catch(() => []),
        ]);
        setTaskCategories(Array.isArray(catsRes) ? catsRes : []);
        setTaskStatuses(Array.isArray(statusRes) ? statusRes : []);
        // Don't load all jobs initially - only load when company is selected
        setAllJobs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const recurrenceSummary = useMemo(
    () =>
      describeRecurrence(
        form.occurrences,
        form.frequency_interval,
        form.frequency_period,
      ),
    [form.occurrences, form.frequency_interval, form.frequency_period],
  );

  const validate = React.useCallback((f) => {
    const v = {};
    if (!String(f.task_name).trim()) v.task_name = "Task name is required";
    if (!String(f.task_code).trim()) v.task_code = "Task code is required";
    else if (!/^[A-Za-z0-9\-_.]+$/.test(String(f.task_code)))
      v.task_code =
        "Only letters, numbers, dash (-), underscore (_) and dot (.) are allowed";
    if (!String(f.task_company_id)) v.task_company_id = "Company is required";
    const overviewText = String(f.task_overview || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    if (overviewText.length < 10)
      v.task_overview = "Description must be at least 10 characters";
    const processTime = Number(f.expected_process_time);
    if (f.expected_process_time === "" || !Number.isFinite(processTime))
      v.expected_process_time = "Expected process time is required";
    else if (processTime < 0)
      v.expected_process_time = "Expected process time cannot be negative";
    return v;
  }, []);

  React.useEffect(() => {
    setErrors(validate(form));
  }, [form, validate]);

  // Backend only enforces the RACI rule when `raci` is sent non-empty; mirror
  // that here so the button disables in lockstep with what the save will do.
  const raciValid = useMemo(
    () => raciRows.length === 0 || getRaciValidation(raciRows).valid,
    [raciRows],
  );

  const canSave = useMemo(() => {
    const noErrors = !errors || Object.keys(errors).length === 0;
    return (
      noErrors &&
      raciValid &&
      String(form.task_name).trim() &&
      String(form.task_code).trim() &&
      String(form.task_company_id)
    );
  }, [form, errors, raciValid]);

  const onBack = () => setActiveSection("task-management");

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const currentErrors = validate(form);
    setErrors(currentErrors);
    if (Object.keys(currentErrors).length > 0 || !canSave || saving) return;
    try {
      setSaving(true);
      const payload = {
        task_name: form.task_name,
        task_code: form.task_code,
        version: form.version === "" ? 0 : Number(form.version),
        task_company_id:
          form.task_company_id === "" ? null : Number(form.task_company_id),
        expected_process_time:
          form.expected_process_time === "" ? null : Number(form.expected_process_time),
        expected_waiting_time:
          form.expected_waiting_time === "" ? null : Number(form.expected_waiting_time),
        expected_rework_time:
          form.expected_rework_time === "" ? null : Number(form.expected_rework_time),
        status_id: form.status_id === "" ? null : Number(form.status_id),
        category_id: form.category_id === "" ? null : Number(form.category_id),
        task_overview: form.task_overview,
        frequency_period: form.frequency_period || "WEEK",
        frequency_interval:
          form.frequency_interval === "" ? 1 : Number(form.frequency_interval),
        occurrences: form.occurrences === "" ? 1 : Number(form.occurrences),
        raci: raciRows,
        taskSkills: (form.skills || [])
          .filter((s) => String(s.name).trim() && String(s.level))
          .map((s) => ({ skill_name: s.name.trim(), level: s.level })),
      };
      await createTask(payload);

      toast.success(`Task "${form.task_name}" has been created successfully!`);
      setActiveSection("task-management");
    } catch (e2) {
      const message = e2?.message || "Failed to create task";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Create Task</h2>
            <p className="page-subtitle">
              Add a new task with basic details and required skills
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onBack}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={onSubmit}
              disabled={!canSave || saving}
            >
              {saving ? "Saving..." : "Create Task"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        {loading ?
          <div className="no-results">Loading...</div>
        : <SideTabs
            defaultActiveId="basic"
            tabs={[
              {
                id: "basic",
                label: "Basic Details",
                content: (
                  <div className="role-card">
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Task Name *</label>
                        <input
                          value={form.task_name}
                          onChange={(e) =>
                            setField("task_name", e.target.value)
                          }
                          placeholder="e.g. Assemble Machine"
                        />
                      </div>
                      <div className="form-group">
                        <label>Task Code *</label>
                        <input
                          value={form.task_code}
                          onChange={(e) =>
                            setField("task_code", e.target.value)
                          }
                          placeholder="e.g. AS-MCH-002"
                        />
                      </div>
                      <div className="form-group">
                        <label>Version</label>
                        <input
                          value={form.version ?? ""}
                          disabled
                          style={{
                            backgroundColor: "#f9fafb",
                            cursor: "not-allowed",
                            color: "#6b7280",
                          }}
                          placeholder="0"
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Process Time (min) *</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_process_time}
                          onChange={(e) =>
                            setField("expected_process_time", e.target.value)
                          }
                          placeholder="e.g. 45"
                        />
                        {errors.expected_process_time && (
                          <div
                            className="input-hint"
                            style={{
                              color: "#b91c1c",
                              fontSize: 12,
                              marginTop: 4,
                            }}
                          >
                            {errors.expected_process_time}
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Expected Waiting Time (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_waiting_time}
                          onChange={(e) =>
                            setField("expected_waiting_time", e.target.value)
                          }
                          placeholder="e.g. 10"
                        />
                      </div>
                      <div className="form-group">
                        <label>Expected Rework Time (min)</label>
                        <input
                          type="number"
                          min={0}
                          value={form.expected_rework_time}
                          onChange={(e) =>
                            setField("expected_rework_time", e.target.value)
                          }
                          placeholder="e.g. 5"
                        />
                      </div>
                      <div className="form-group">
                        <label>Task Status</label>
                        <select
                          value={form.status_id}
                          onChange={(e) =>
                            setField("status_id", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>
                          {taskStatuses.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.description || `Status ${s.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Task Category</label>
                        <select
                          value={form.category_id}
                          onChange={(e) =>
                            setField("category_id", e.target.value)
                          }
                        >
                          <option value="">Select task category</option>
                          {taskCategories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.description || c.name || `Category ${c.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Company *</label>
                        <select
                          value={form.task_company_id}
                          onChange={(e) => {
                            const val = e.target.value;
                            setField("task_company_id", val);
                            loadJobsForCompany(val);
                            // Reset RACI matrix when company changes (job pool changes)
                            setRaciRows([]);
                          }}
                        >
                          <option value="">Select Company</option>
                          {companies.map((c) => (
                            <option
                              key={c.company_id || c.id}
                              value={c.company_id || c.id}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label title="Total number of times this task should occur within the recurrence window">
                          Occurrences
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.occurrences}
                          onChange={(e) =>
                            setField("occurrences", e.target.value)
                          }
                          placeholder="e.g. 2"
                        />
                        <small className="hint">
                          Number of times this task repeats within one
                          recurrence window
                        </small>
                      </div>
                      <div className="form-group">
                        <label title="How many periods to wait between occurrences (e.g. 3 means every 3 weeks)">
                          Repeat Interval
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={form.frequency_interval}
                          onChange={(e) =>
                            setField("frequency_interval", e.target.value)
                          }
                          placeholder="e.g. 3"
                        />
                        <small className="hint">
                          Gap between recurrence windows, counted in the unit
                          set below — e.g. 3 with "Week" means every 3 weeks
                        </small>
                      </div>
                      <div className="form-group">
                        <label title="Unit of time used for the repeat interval (Day, Week, Month, or Year)">
                          Period
                        </label>
                        <select
                          value={form.frequency_period}
                          onChange={(e) =>
                            setField("frequency_period", e.target.value)
                          }
                        >
                          {FREQUENCY_PERIODS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                        <small className="hint">
                          Time unit the repeat interval is measured in
                        </small>
                      </div>
                      <div className="form-group full">
                        <label>Recurrence Summary</label>
                        <input value={recurrenceSummary} disabled />
                      </div>
                      <div className="form-group full">
                        <label>Associated Processes</label>
                        <div className="chip-list">
                          <span className="muted">
                            Processes are managed elsewhere and are read-only
                            here.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                id: "description",
                label: "Description",
                content: (
                  <div className="role-card">
                    <RichTextEditor
                      label="Task Description *"
                      value={form.task_overview}
                      onChange={(val) => setField("task_overview", val)}
                      height={280}
                    />
                    {errors.task_overview && (
                      <div
                        className="input-hint"
                        style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}
                      >
                        {errors.task_overview}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                id: "skills",
                label: "Req Skills",
                content: (
                  <div className="role-card">
                    <div
                      className="section-title"
                      style={{
                        marginBottom: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>Req Skills</span>
                      <button
                        type="button"
                        className="primary-btn icon-square"
                        onClick={() => setShowAddSkill(true)}
                        aria-label="Add Skill"
                        title="Add Skill"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 5v14M5 12h14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                    <SkillTable
                      mode="manage"
                      skills={form.skills}
                      levels={LEVELS}
                      onDelete={(idx) =>
                        setForm((prev) => ({
                          ...prev,
                          skills: prev.skills.filter((_, i) => i !== idx),
                        }))
                      }
                    />
                    <SkillAddModal
                      open={showAddSkill}
                      onClose={() => setShowAddSkill(false)}
                      levels={LEVELS}
                      onSave={(s) => {
                        setForm((prev) => ({
                          ...prev,
                          skills: [...prev.skills, s],
                        }));
                        setShowAddSkill(false);
                      }}
                    />
                  </div>
                ),
              },
              {
                id: "raci",
                label: "RACI",
                content: (
                  <RaciMatrix
                    mode="manage"
                    jobs={allJobs}
                    rows={raciRows}
                    onChange={setRaciRows}
                    label={form.task_name}
                    disabled
                    disabledMessage="Save this task first, then link jobs and assign RACI roles from the task's edit page."
                  />
                ),
              },
            ]}
          />
        }
      </div>
    </div>
  );
};

export default TaskCreatePage;
